import React, { useCallback, useMemo } from 'react';
import { useToast, MenuItem } from '@app/core';
import {
  useCurrentOrganization,
  useCurrentOrganizationId,
  useRemoveAccountFromOrganization,
} from 'queries';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { Client } from 'store';
import { Colors, Theme } from '@app/theme';
import { MemberItem } from './MemberItem';

const useStyles = createUseStyles((theme: Theme) => ({
  removeButton: {
    color: theme.colors.redAccessible,
  },
}));

interface Props {
  member: Client;
}

export const TeamMemberItem = ({ member }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const toast = useToast();
  const { t } = useTranslation();
  const organizationId = useCurrentOrganizationId();
  const { data: organization } = useCurrentOrganization();

  const { mutateAsync: removeAccount } =
    useRemoveAccountFromOrganization(organizationId);

  const organizationOwnerId = organization ? organization.ownerId : null;
  const isAdmin = member.id === organizationOwnerId;

  const remove = useCallback(async () => {
    try {
      await removeAccount({ accountId: member.accountId });
      toast.success(t(`teamInformations.memberRemoved`));
    } catch (e) {
      toast.exception(e);
    }
  }, [removeAccount, toast, t, member]);

  const chip = useMemo(
    () => ({
      color: (isAdmin ? 'purple' : 'blue') as Colors,
      text: t(`teamInformations.roles.${isAdmin ? 'admin' : 'member'}`),
    }),
    [isAdmin],
  );

  return (
    <MemberItem email={member.account.email} chip={chip}>
      <MenuItem onClick={remove}>
        <div className={classes.removeButton}>
          {t('teamInformations.remove')}
        </div>
      </MenuItem>
    </MemberItem>
  );
};
